/**
 * Placement writes — chunk 2, items 3, 6 and 8.
 *
 * Contract mirror: labourcore-api/src/modules/placements/.
 *
 * ── What these replace ────────────────────────────────────────────────────
 *
 * All three ran through the generic `table()` factory, which emits
 * `UPDATE|INSERT … WHERE id = $1` with no tenant scope, on tables where RLS is
 * disabled. That is the shape of Live-Defects-Log #27:
 *
 *   1. `placements.update(id, { stage })` — any authenticated caller who knew a
 *      placement id could cancel or complete another company's placement.
 *   2. `placements.insert({ partner_company_id, coordinator_company_id, … })` —
 *      nothing checked that the caller belonged to the proposing company, that
 *      the operative was on its books, or that the coordinator company was the
 *      requirement's real owner.
 *   3. `placement_ratings.rate()` decided `rater_side` and `rated_company_id` in
 *      the browser from `getActiveCompanyId()` — the unordered membership cache
 *      GH #167 established as unsafe — and sent both, along with
 *      `rated_by_user_id` read from the browser's own session.
 *
 * ── What is deliberately NOT decided here ─────────────────────────────────
 *
 * Which of a caller's companies is proposing (PD-2's family), and which side a
 * user who belongs to BOTH companies on a placement is rating from (PD-3). The
 * second returns 409 rather than a guess, because the rating row is permanent.
 */

import { apiGet, apiPost } from "./client";

export type Row = Record<string, unknown>;

/** Move a placement to a new stage. 404 when the caller is not party to it. */
export function setPlacementStage(
  placementId: string,
  stage: string,
  completedAt?: string | null,
): Promise<Row> {
  return apiPost<Row>(`/api/placements/${placementId}/stage`, {
    stage,
    completed_at: completedAt ?? null,
  });
}

export interface ProposeBody {
  requirement_id: string;
  operative_id: string;
  /** Still the caller's choice — see PD-2 — but validated against memberships. */
  partner_company_id: string;
  /** Must match the requirement's real owner; the modal copies it from the requirement. */
  coordinator_company_id: string;
  agreed_day_rate?: number | null;
  stage?: string | null;
  notes?: string | null;
}

export function proposeOperative(body: ProposeBody): Promise<Row> {
  return apiPost<Row>("/api/placements/propose", body);
}

/** Operatives on the caller's books, optionally narrowed to one of their companies. */
export function fetchOperatives(companyId?: string | null): Promise<Row[]> {
  return apiGet<Row[]>("/api/operatives", companyId ? { company_id: companyId } : {});
}

export interface CompanyScores {
  reliability_score: number | null;
  performance_score: number | null;
  payment_score: number | null;
}

export interface RatingContext {
  /** Derived from memberships, not sent by the client. Null when ambiguous. */
  rater_side: "coordinator" | "partner" | null;
  rated_company_id: string | null;
  /** True when the caller belongs to both companies on the placement — PD-3. */
  ambiguous: boolean;
  scores: CompanyScores | null;
}

export function fetchRatingContext(placementId: string): Promise<RatingContext> {
  return apiGet<RatingContext>(`/api/placements/${placementId}/rating-context`);
}

export interface RateBody {
  reliability?: number | null;
  quality?: number | null;
  comms?: number | null;
  safety?: number | null;
  rehire?: number | null;
  payment_timeliness?: number | null;
  site_conditions?: number | null;
  comment?: string | null;
}

/**
 * Record a rating. Returns the stored row and the counterparty's scores as they
 * stand after the recompute trigger — read in the same transaction, so no
 * follow-up request is needed to show the delta.
 */
export function ratePlacement(
  placementId: string,
  body: RateBody,
): Promise<{ rating: Row; scores: CompanyScores | null }> {
  return apiPost<{ rating: Row; scores: CompanyScores | null }>(
    `/api/placements/${placementId}/rate`,
    body,
  );
}

/** Disputes filed against a placement, each with the person who filed it. */
export function fetchPlacementDisputes(placementId: string): Promise<Row[]> {
  return apiGet<Row[]>(`/api/placements/${placementId}/disputes`);
}

/** Ratings left on a placement, each with the person who left it. */
export function fetchPlacementRatings(placementId: string): Promise<Row[]> {
  return apiGet<Row[]>(`/api/placements/${placementId}/ratings`);
}
